import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/FileTools";
import { Building2, Lock, MapPin, Pencil } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import { propertyTypeLabel } from "@/lib/propertyTypes";
import Money from "@/components/Money";
import EditPropertyDialog from "@/components/EditPropertyDialog";

export default function PropertyTile({ p, onRefresh }) {
  const { lang, user } = useApp();
  const locked = p.locked;
  const isAdmin = user?.role === "admin";
  const [editOpen, setEditOpen] = useState(false);
  return (
    <>
      <Link to={`/properties/${p.id}`} className="block group" data-testid={`marketplace-prop-link-${p.id}`}>
        <Card
          className="bg-[#FFFAF0] border-[#D8CFBB] overflow-hidden rounded-lg hover:border-[#C5A880] hover:shadow-md transition-all duration-200 cursor-pointer"
          data-testid={`marketplace-prop-${p.id}`}
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-[#EBE3D3]/40">
            {p.image_paths?.[0] && !locked && (
              <AuthImage
                fileId={p.image_paths[0]}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
            {(!p.image_paths?.[0] || locked) && (
              <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#F0E7D3] to-[#F5F0E6]">
                {locked ? <Lock className="h-8 w-8 text-[#B8A98F]" /> : <Building2 className="h-8 w-8 text-[#C4B99E]" />}
              </div>
            )}
            <Badge className="absolute top-3 start-3 bg-[#C5A880] text-black text-[10px] tracking-widest">{t("off_market", lang)}</Badge>
            {isAdmin && (
              <Button
                size="sm"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditOpen(true); }}
                data-testid={`admin-marketplace-edit-${p.id}`}
                title="Modifica (admin)"
                className="absolute top-3 end-3 h-8 w-8 p-0 rounded-full bg-white/95 hover:bg-white text-[#B8965A] shadow-md z-10"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          <div className="p-5">
            {p.property_type && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1.5">
                {propertyTypeLabel(p.property_type, lang)}
              </p>
            )}
            <h4
              className="text-lg font-light mb-1 text-[#1A1610] group-hover:text-[#C5A880] transition-colors"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {p.title}
            </h4>
            <p className="text-xs text-[#7A6E5A] mb-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {p.city}, {p.country}
            </p>
            <p className="text-[11px] text-[#9A8B72] mb-3">by {p.agent_name}</p>
            <Money value={p.price} className="text-lg text-[#C5A880]" />
            {locked && <p className="text-xs text-amber-700 mt-3">{t("locked", lang)}</p>}
          </div>
        </Card>
      </Link>
      {isAdmin && (
        <EditPropertyDialog
          property={editOpen ? p : null}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onRefresh?.(); }}
        />
      )}
    </>
  );
}
